import { useState } from 'react';
import { useMutation } from '@apollo/client';
import { CheckCircle2, CircleAlert, Globe2 } from 'lucide-react';
import { UPDATE_TIME_ZONE_MUTATION } from '../graphql/mutations';
import { deviceTimeZone } from '../lib/timezone';

interface TimeZoneSettingProps {
  /** The stored `User.timeZone`, or null/undefined when never set (UTC fallback). */
  currentTimeZone: string | null | undefined;
}

/** "Europe/London" → "Europe / London" — readable without losing the IANA name. */
function friendlyZone(zone: string): string {
  return zone.replace(/_/g, ' ').replace(/\//g, ' / ');
}

/**
 * The "Time zone" card on Profile. The stored zone is what the agent's
 * "today" and every reminders.md due time resolve in, so it must be visible
 * and one tap to fix: when the device's zone differs from the stored one
 * (or nothing is stored — the UTC fallback), offer "Use this device's time
 * zone". No free-form picker — the device already knows the right answer,
 * and a wrong hand-picked zone is the only way this setting can hurt.
 */
export function TimeZoneSetting({ currentTimeZone }: TimeZoneSettingProps) {
  const device = deviceTimeZone();
  const [confirmation, setConfirmation] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);

  const [updateTimeZone, { loading: saving }] = useMutation<{
    updateTimeZone: { id: string; timeZone: string | null };
  }>(UPDATE_TIME_ZONE_MUTATION);

  const handleUseDevice = async () => {
    if (!device) return;
    setFailed(false);
    setConfirmation(null);
    try {
      await updateTimeZone({ variables: { timeZone: device } });
      setConfirmation(`Time zone set to ${friendlyZone(device)}.`);
    } catch {
      setFailed(true);
    }
  };

  const offerDevice = device !== null && device !== currentTimeZone;

  return (
    <section
      aria-label="Time zone"
      className="glass-panel rounded-2xl p-5 sm:p-6 border border-[var(--card-border)] shadow-soft animate-fade-in-up"
    >
      <div className="flex items-center gap-3 mb-1">
        <Globe2 className="w-5 h-5 text-[var(--text-secondary)] shrink-0" aria-hidden />
        <h2 className="text-lg font-semibold text-[var(--text-primary)]">Time zone</h2>
      </div>

      <p className="text-sm text-[var(--text-secondary)]">
        {currentTimeZone ? (
          <>
            Your times resolve in <strong>{friendlyZone(currentTimeZone)}</strong> — journal days,
            &ldquo;today&rdquo; in chat, and when reminders arrive.
          </>
        ) : (
          <>
            No time zone is set, so times fall back to <strong>UTC</strong> — reminders would arrive
            at the wrong hour. Set it below.
          </>
        )}
      </p>

      <div role="status" aria-live="polite">
        {confirmation && (
          <p className="mt-3 flex items-start gap-2 text-sm text-[var(--text-secondary)]">
            <CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0 text-emerald-600" aria-hidden />
            <span>{confirmation}</span>
          </p>
        )}
      </div>

      {failed && (
        <p role="alert" className="mt-3 flex items-start gap-2 text-sm text-amber-700">
          <CircleAlert className="w-4 h-4 mt-0.5 shrink-0" aria-hidden />
          <span>That didn&apos;t save. Check your connection and try again.</span>
        </p>
      )}

      {offerDevice && (
        <button
          type="button"
          className="btn btn-primary mt-4"
          onClick={() => void handleUseDevice()}
          disabled={saving}
          aria-busy={saving}
        >
          {saving ? 'Saving…' : `Use this device's time zone (${friendlyZone(device)})`}
        </button>
      )}
    </section>
  );
}
